import React, { FC, useState } from 'react';
import Swal from 'sweetalert2';
import { Grid, TextField } from '@mui/material';
import { SCUIButton, SCUILoadingLinear } from '../../../../atomic/atoms';
import { SCUIGeneralDrawerHeader } from '../../../../atomic/molecules';
import { useTranslation } from 'react-i18next';
import { updateCustomerAddressRequest } from '../../CustomerManagement.helper';
import { CustomerCommand } from '../../../../services/command';
import { CustomerAddressEntityFields } from './CustomerAddressEntityFields';
import { CAIP } from './customerAddressInformationPaper.classes';

interface IProps {
    detail?: any;
    onClose?: any;
    setRefresh?: any;
    addressEntityTypeListByCountryId?: any;
}
export const GenericCustomerAddress: FC<IProps> = ({
    detail,
    onClose,
    setRefresh,
    addressEntityTypeListByCountryId,
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [customerAddress, setCustomerAddress] = useState<updateCustomerAddressRequest>(detail);
    const PHONE_MAX_LENGTH = 15;

    const UpdateCustomerAddress = async () => {
        const phoneNumber = (customerAddress?.phoneNumber ?? '').trim();
        const mobilePhoneNumber = (customerAddress?.mobilePhoneNumber ?? '').trim();
        if (phoneNumber.length > PHONE_MAX_LENGTH || mobilePhoneNumber.length > PHONE_MAX_LENGTH) {
            Swal.fire(t('customerInformation.phoneNumberMaxLength'), '', 'error');
            return;
        }
        setLoading(true);
        CustomerCommand.UpdateCustomerAddress(customerAddress)
            .then((res: any) => {
                if (res) {
                    if (res.statusCode !== 200 && res?.error !== null && res.error?.length > 0) {
                        Swal.fire(res?.error, ' ', 'error');
                        return;
                    }
                    if (!res?.model?.operationStatus) {
                        Swal.fire(res?.model?.errorMessageKeyword, ' ', 'error');
                        return;
                    } else {
                        Swal.fire('', ' ', 'success');
                        setRefresh((prevLoading: any) => !prevLoading);
                    }
                }
            })
            .finally(() => {
                setLoading(false);
                onClose();
            });
    };

    return (
        <>
            <SCUIGeneralDrawerHeader header={t('general.edit')} onClose={onClose} />
            <SCUILoadingLinear display={loading} />
            <Grid container spacing={1} className={CAIP.drawerForm}>
                <p className={`subheadtxt ${CAIP.fieldAddressNumber}`}>
                    {t('addressDetail.AddressNumber')}: {detail?.customerAddressId}
                </p>
                <Grid
                    sx={{ marginTop: 0.5 }}
                    size={{
                        xs: 12,
                        md: 12,
                        lg: 12,
                        xl: 12
                    }}>
                    <TextField
                        className={CAIP.inputTextfieldAddressDefinition}
                        defaultValue={customerAddress?.addressDefinition}
                        onChange={(e) =>
                            setCustomerAddress({
                                ...customerAddress,
                                addressDefinition: e.target.value.trim(),
                            })
                        }
                        label={t('addressDetail.AddressDefinition')}
                        slotProps={{ inputLabel: { shrink: true } }}
                        fullWidth
                    />
                </Grid>
                <Grid
                    sx={{ marginTop: 0.5 }}
                    size={{
                        xs: 12,
                        md: 12,
                        lg: 12,
                        xl: 12
                    }}>
                    <TextField
                        className={CAIP.inputTextfieldFirstName}
                        defaultValue={customerAddress?.firstName}
                        onChange={(e) =>
                            setCustomerAddress({
                                ...customerAddress,
                                firstName: e.target.value.trim(),
                            })
                        }
                        label={t('general.name')}
                        slotProps={{ inputLabel: { shrink: true } }}
                        fullWidth
                    />
                </Grid>
                <Grid
                    sx={{ marginTop: 0.5 }}
                    size={{
                        xs: 12,
                        md: 12,
                        lg: 12,
                        xl: 12
                    }}>
                    <TextField
                        className={CAIP.inputTextfieldLastName}
                        defaultValue={customerAddress?.lastName}
                        onChange={(e) =>
                            setCustomerAddress({
                                ...customerAddress,
                                lastName: e.target.value.trim(),
                            })
                        }
                        label={t('general.surname')}
                        slotProps={{ inputLabel: { shrink: true } }}
                        fullWidth
                    />
                </Grid>

                {/* Address Entity Fields Component */}
                <CustomerAddressEntityFields
                    customerAddress={customerAddress}
                    setCustomerAddress={setCustomerAddress}
                    addressEntityTypeListByCountryId={addressEntityTypeListByCountryId}
                />

                <Grid
                    sx={{ marginTop: 0.5 }}
                    size={{
                        xs: 12,
                        md: 12,
                        lg: 12,
                        xl: 12
                    }}>
                    <TextField
                        className={CAIP.inputTextfieldPhoneAreaCode}
                        value={customerAddress?.phoneAreaCode || ''}
                        onChange={(e) => {
                            const numericValue = e.target.value.replace(/[^0-9+]/g, '');
                            setCustomerAddress({
                                ...customerAddress,
                                phoneAreaCode: numericValue.trim(),
                            });
                        }}
                        label={t('customerInformation.phoneAreaCode')}
                        slotProps={{ inputLabel: { shrink: true } }}
                        fullWidth
                        disabled
                    />
                </Grid>
                <Grid
                    sx={{ marginTop: 0.5 }}
                    size={{
                        xs: 12,
                        md: 12,
                        lg: 12,
                        xl: 12
                    }}>
                    <TextField
                        className={CAIP.inputTextfieldPhoneNumber}
                        value={customerAddress?.phoneNumber || ''}
                        onChange={(e) => {
                            const numericValue = e.target.value.replace(/[^0-9]/g, '');
                            setCustomerAddress({
                                ...customerAddress,
                                phoneNumber: numericValue.trim(),
                            });
                        }}
                        label={t('general.phoneNumber')}
                        slotProps={{
                            inputLabel: { shrink: true },
                            htmlInput: { pattern: '[0-9]*' },
                        }}
                        fullWidth
                    />
                </Grid>
                {
                    <Grid
                        sx={{ marginTop: 0.5 }}
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 12,
                            xl: 12
                        }}>
                        <TextField
                            className={CAIP.inputTextfieldMobilePhoneNumber}
                            value={customerAddress?.mobilePhoneNumber || ''}
                            onChange={(e) => {
                                const numericValue = e.target.value.replace(/[^0-9]/g, '');
                                setCustomerAddress({
                                    ...customerAddress,
                                    mobilePhoneNumber: numericValue.trim(),
                                });
                            }}
                            label={'Mobil ' + t('general.phoneNumber')}
                            slotProps={{
                                inputLabel: { shrink: true },
                                htmlInput: { pattern: '[0-9]*' },
                            }}
                            fullWidth
                        />
                    </Grid>
                }
                <Grid
                    sx={{ marginTop: 0.5 }}
                    size={{
                        xs: 12,
                        md: 12,
                        lg: 12,
                        xl: 12
                    }}>
                    <TextField
                        className={CAIP.inputTextfieldAddress}
                        defaultValue={customerAddress?.address}
                        multiline
                        rows={5}
                        label={t('general.address')}
                        slotProps={{ inputLabel: { shrink: true } }}
                        onChange={(e) =>
                            setCustomerAddress({
                                ...customerAddress,
                                address: e.target.value.trim(),
                            })
                        }
                        fullWidth
                    />
                </Grid>
                <Grid
                    sx={{ marginTop: 0.5 }}
                    size={{
                        xs: 12,
                        md: 12,
                        lg: 12,
                        xl: 12
                    }}>
                    <SCUIButton
                        className={CAIP.inputButtonUpdate}
                        text={t('general.update')}
                        type="primary"
                        sizeType="fixed"
                        buttonType="submit"
                        size={'m'}
                        fullwidth
                        onClick={UpdateCustomerAddress}
                    />
                </Grid>
            </Grid>
        </>
    );
};
